import { useEffect, useMemo, useState } from 'react';
import { Copy, Search } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { SCHOOL_STATUS_LABEL, SCHOOL_STATUS_TONE } from '@/constants/institute';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { cn, getNameInitials } from '@/lib/utils';
import type { InstituteListItem } from '@/types/institute';
import { featuresApi } from '../api/features.api';

interface CopyFeaturesModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The institute being copied *from* — the board on screen. */
  source: { uuid: string; displayName: string };
  /** How many requirements would travel. Zero closes the door on the action. */
  featureCount: number;
  /** Everyone the picker has loaded; the source is filtered out below. */
  institutes: InstituteListItem[];
}

/**
 * Handing one institute's requirements to another.
 *
 * The case this exists for: a group opens a second school, or a college is sold
 * the same package as the one next door. Ticking twenty features again by hand
 * is how the second one quietly ends up with nineteen.
 *
 * Two things the dialog is careful to say, because both are easy to assume
 * wrongly:
 *
 *   it only adds       a target keeps everything it already had, and a feature
 *                      it already tracks is left exactly as it stands.
 *   the switch stays   a copied requirement arrives inactive, because what
 *                      one customer may use is not decided for another.
 *                      Carrying it across is a deliberate tick.
 */
export function CopyFeaturesModal({
  isOpen,
  onClose,
  source,
  featureCount,
  institutes,
}: CopyFeaturesModalProps) {
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [includeState, setIncludeState] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Fresh every time it opens: the last set somebody chose is never what they
  // mean the next time, and a stale tick here writes to a real customer.
  useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setChosen(new Set());
    setIncludeState(false);
  }, [isOpen]);

  const candidates = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return institutes
      .filter((institute) => institute.uuid !== source.uuid)
      .filter(
        (institute) =>
          !needle ||
          institute.displayName.toLowerCase().includes(needle) ||
          institute.code.toLowerCase().includes(needle),
      );
  }, [institutes, search, source.uuid]);

  function toggle(uuid: string) {
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }

  async function copy() {
    if (chosen.size === 0 || isSaving) return;
    setIsSaving(true);

    try {
      const result = await featuresApi.copyTo(source.uuid, {
        targetInstituteUuids: [...chosen],
        includeState,
      });

      const added = result.targets.reduce((total, target) => total + target.added, 0);
      const skipped = result.targets.reduce((total, target) => total + target.alreadyTracked, 0);

      toast.success(
        added === 0
          ? 'Nothing new to copy'
          : `${added} requirement${added === 1 ? '' : 's'} copied`,
        skipped > 0
          ? `${skipped} were already on those trackers and were left alone.`
          : `From ${result.from.displayName} to ${result.targets.length} institute${
              result.targets.length === 1 ? '' : 's'
            }.`,
      );
      onClose();
    } catch (caught) {
      // The server's messages name the reason — an institute that has gone, a
      // source with nothing on it — so they are shown as they are written.
      toast.error(
        'Could not copy those requirements',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !isSaving && onClose()}
      title="Copy requirements"
      description={`Gives another institute the ${featureCount} feature${
        featureCount === 1 ? '' : 's'
      } ${source.displayName} asked for.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => void copy()}
            isLoading={isSaving}
            disabled={chosen.size === 0}
            leftIcon={<Copy className="h-4 w-4" />}
          >
            Copy to {chosen.size || 'no'} institute{chosen.size === 1 ? '' : 's'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Search institutes"
          hideLabel
          placeholder="Search by name or code"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          leftIcon={<Search className="h-4 w-4" />}
        />

        <div className="max-h-72 overflow-y-auto rounded-field border border-line">
          {candidates.length === 0 ? (
            <EmptyState
              title="No other institutes match"
              description="Every other institute is filtered out by that search."
            />
          ) : (
            <ul className="divide-y divide-line">
              {candidates.map((institute) => {
                const isChosen = chosen.has(institute.uuid);

                return (
                  <li key={institute.uuid}>
                    <label
                      className={cn(
                        'flex cursor-pointer items-center gap-3 p-3 transition-colors duration-150',
                        isChosen ? 'bg-accent-soft' : 'hover:bg-surface-2',
                      )}
                    >
                      <Checkbox
                        label={`Copy to ${institute.displayName}`}
                        hideLabel
                        checked={isChosen}
                        disabled={isSaving}
                        onChange={() => toggle(institute.uuid)}
                      />

                      <span
                        aria-hidden
                        className="grid h-8 w-8 shrink-0 place-items-center rounded-field bg-surface-2 text-2xs font-[650] text-ink-2"
                      >
                        {getNameInitials(institute.displayName)}
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-[560] text-ink">
                          {institute.displayName}
                        </span>
                        <span className="block truncate text-2xs text-ink-3">
                          {institute.code} · {institute.city}
                        </span>
                      </span>

                      <Badge tone={SCHOOL_STATUS_TONE[institute.status]}>
                        {SCHOOL_STATUS_LABEL[institute.status]}
                      </Badge>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="rounded-field border border-line bg-surface-2 p-3">
          <Checkbox
            label="Copy the active switch too"
            checked={includeState}
            disabled={isSaving}
            onChange={(event) => setIncludeState(event.target.checked)}
          />
          <p className="mt-1.5 pl-6 text-xs text-ink-3">
            {includeState
              ? 'Anything active here will be active there — only choose this for a branch being set up to match.'
              : 'Copied requirements arrive inactive. What one institute may use is not decided for another.'}
          </p>
        </div>

        <p className="text-xs text-ink-3">
          Copying only ever adds. Nothing already on the other trackers is removed or changed.
        </p>
      </div>
    </Modal>
  );
}
